import React, { createContext, useContext, useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Label, PaginatedData } from "../types";
import { 
  fetchReleasesForLabels, 
  fetchAllLabelsByListenStatus 
} from "../utils/supabase";
import { scrollToTop } from "../utils/scrollUtils";
import { useAuth } from "./AuthContext";

export type FilterType = 'checked' | 'unchecked' | 'all';

export interface LabelsContextType {
  paginatedLabels: PaginatedData<Label>;
  setLabelPage: (page: number) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  isLoading: boolean;
  error: string | null;
  filterType: FilterType;
  setFilterType: (type: FilterType) => void;
}

export const LabelsContext = createContext<LabelsContextType>({
  paginatedLabels: {
    data: [],
    total: 0,
    currentPage: 1,
    totalPages: 1,
    pageSize: 5
  },
  setLabelPage: () => {},
  searchTerm: "",
  setSearchTerm: () => {},
  isLoading: false,
  error: null,
  filterType: 'all',
  setFilterType: () => {}
});

const LABELS_PER_PAGE = 5;

// Utility function to shuffle an array consistently based on a seed
const shuffleArray = <T,>(array: T[], seed: number): T[] => {
  const arrayCopy = [...array]; // Create a copy to avoid mutating the original
  let currentIndex = arrayCopy.length;
  let temporaryValue;
  let randomIndex;

  // Generate a seeded random number
  const seededRandom = () => {
    const x = Math.sin(seed + currentIndex) * 10000;
    return x - Math.floor(x);
  };

  // While there remain elements to shuffle
  while (currentIndex !== 0) {
    // Pick a remaining element
    randomIndex = Math.floor(seededRandom() * currentIndex);
    currentIndex -= 1;

    // Swap it with the current element
    temporaryValue = arrayCopy[currentIndex];
    arrayCopy[currentIndex] = arrayCopy[randomIndex];
    arrayCopy[randomIndex] = temporaryValue;
  }

  return arrayCopy;
};

export const LabelsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { userId, isAuthenticated } = useAuth();
  const [allLabels, setAllLabels] = useState<Label[]>([]);
  const [currentLabelPage, setCurrentLabelPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // Add filterType state for tabs
  const [filterType, setFilterType] = useState<FilterType>('all');

  // Add randomization seed that changes on each page load
  const [randomizationSeed] = useState<number>(() => Math.random() * 10000);
  const currentPageLabelsRef = useRef<Label[]>([]);
  // Add a state to track if initial data has been loaded
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  
  // Store the full list of labels for client-side pagination and filtering
  const [allFilteredLabels, setAllFilteredLabels] = useState<Label[]>([]);
  
  // Store all fetched labels to avoid re-fetching when filter/search changes
  const [allFetchedLabels, setAllFetchedLabels] = useState<Record<FilterType, Label[]>>({
    checked: [],
    unchecked: [],
    all: []
  });

  // Track which filter types have been fetched to avoid infinite loops
  const [fetchedFilters, setFetchedFilters] = useState<Set<FilterType>>(new Set());
  
  // Filter and paginate labels based on current filter and search
  const paginatedLabels = useMemo(() => {
    // Calculate total pages based on filtered labels
    const totalPages = Math.max(1, Math.ceil(allFilteredLabels.length / LABELS_PER_PAGE));
    
    // Return the current state
    return {
      data: allLabels,
      total: allFilteredLabels.length,
      currentPage: currentLabelPage,
      totalPages: totalPages,
      pageSize: LABELS_PER_PAGE
    };
  }, [allLabels, allFilteredLabels, currentLabelPage]);

  // Update the ref whenever allLabels changes
  useEffect(() => {
    currentPageLabelsRef.current = allLabels;
  }, [allLabels]);

  // Basic function to load page data
  const loadPageData = useCallback(async (pageNumber: number, labels: Label[]) => {
    if (!userId || !isAuthenticated) return;
    
    // Skip if no labels are available
    if (labels.length === 0) return;
    
    setIsLoading(true);
    
    try {
      // Get the current page of labels
      const startIndex = (pageNumber - 1) * LABELS_PER_PAGE;
      const endIndex = startIndex + LABELS_PER_PAGE;
      const pageLabels = labels.slice(startIndex, endIndex);
      
      const labelIds = pageLabels.map(label => label.id!);
      
      // Initialize labels with loading state
      const initializedLabels = pageLabels.map(label => ({
        ...label,
        releases: [],
        isLoading: true
      }));
      
      // Set the current page data
      setAllLabels(initializedLabels);
      
      // Load the releases for these labels
      const releasesMap = await fetchReleasesForLabels(labelIds);
      
      // Update the labels with their releases
      setAllLabels(initializedLabels.map(label => ({
        ...label,
        releases: releasesMap.get(label.id!) || [],
        isLoading: false
      })));
      
    } catch (err) {
      console.error('Error loading page data:', err);
      setError('Failed to load labels. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [userId, isAuthenticated]);
  
  // Effect for filter type changes
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!userId || !isAuthenticated) return;
    
    // Clear any existing labels data
    setAllLabels([]);
    
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        let labels: Label[];

        // Use cached data if already fetched
        if (fetchedFilters.has(filterType)) {
          labels = allFetchedLabels[filterType];
        } else {
          // Fetch new data if needed
          labels = await fetchAllLabelsByListenStatus(filterType);

          // Update the cache
          setAllFetchedLabels(prev => ({
            ...prev,
            [filterType]: labels
          }));

          // Mark this filter as fetched
          setFetchedFilters(prev => new Set(prev).add(filterType));
        }
        
        // Apply search filter if searchTerm exists
        let filteredLabels = labels;
        if (searchTerm) {
          const lowercaseSearch = searchTerm.toLowerCase();
          filteredLabels = labels.filter(label =>
            label.label_name.toLowerCase().includes(lowercaseSearch) ||
            label.label_url.toLowerCase().includes(lowercaseSearch)
          );
        }

        // Shuffle labels before setting them
        const shuffledLabels = shuffleArray(filteredLabels, randomizationSeed);

        // Update filtered labels
        setAllFilteredLabels(shuffledLabels);

        // Reset to page 1
        setCurrentLabelPage(1);

        // Load the first page of data
        await loadPageData(1, shuffledLabels);
        
        setIsInitialDataLoaded(true);
      } catch (err) {
        console.error('Error fetching labels:', err);
        setError('Failed to load labels. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  // Note: allFetchedLabels is intentionally excluded to prevent infinite loops when updating the cache
  }, [userId, isAuthenticated, filterType, fetchedFilters, loadPageData, randomizationSeed, searchTerm]);
  /* eslint-enable react-hooks/exhaustive-deps */
  
  // Effect for search term changes
  useEffect(() => {
    if (!isInitialDataLoaded) return;
    
    // Apply search filter
    let filteredLabels = [...allFetchedLabels[filterType]];
    
    if (searchTerm) {
      const lowercaseSearch = searchTerm.toLowerCase();
      filteredLabels = filteredLabels.filter(label => 
        label.label_name.toLowerCase().includes(lowercaseSearch) ||
        label.label_url.toLowerCase().includes(lowercaseSearch)
      );
    }
    
    // Shuffle the filtered labels
    const shuffledLabels = shuffleArray(filteredLabels, randomizationSeed);
    
    // Update filtered labels
    setAllFilteredLabels(shuffledLabels);
    
    // Reset to page 1
    setCurrentLabelPage(1);
    
    // Load the first page of data
    loadPageData(1, shuffledLabels);
  }, [searchTerm, isInitialDataLoaded, filterType, allFetchedLabels, loadPageData, randomizationSeed]);
  
  // Page navigation function
  const setLabelPage = useCallback((page: number) => {
    // Skip if already on this page
    if (page === currentLabelPage) return;
    
    // Update the current page
    setCurrentLabelPage(page);
    
    // Load the data for this page
    loadPageData(page, allFilteredLabels);
    
    // Scroll to top
    scrollToTop();
  }, [currentLabelPage, allFilteredLabels, loadPageData]);
  
  // Filter type change handler
  const handleFilterTypeChange = useCallback((type: FilterType) => {
    if (type !== filterType) {
      setFilterType(type);
      // The effect for filter type changes will handle the rest
    }
  }, [filterType]);

  // Update context value to include new props
  const contextValue = useMemo(
    () => ({
      paginatedLabels,
      setLabelPage,
      searchTerm,
      setSearchTerm,
      isLoading,
      error,
      filterType,
      setFilterType: handleFilterTypeChange
    }),
    [
      paginatedLabels,
      setLabelPage,
      searchTerm,
      setSearchTerm,
      isLoading,
      error,
      filterType,
      handleFilterTypeChange
    ]
  );

  return (
    <LabelsContext.Provider value={contextValue}>
      {children}
    </LabelsContext.Provider>
  );
};

// Custom hook for consuming the labels context
export const useLabels = () => {
  const context = useContext(LabelsContext);
  if (!context) {
    throw new Error("useLabels must be used within a LabelsProvider");
  }
  return context;
};

export default LabelsProvider; 